import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button, buttonVariants } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectOption } from '../../components/ui/select';
import {
  Loader2, CheckCircle2, AlertCircle, Calendar, Clock, MapPin,
  Video, Users, Phone, MessageCircle, GraduationCap, ArrowLeft,
} from 'lucide-react';
import { registrationFormService } from '../../utils/api';
import { attributionQuery, submissionToken } from '../../utils/formChannels';
import { nationalIdError } from '../../utils/nationalId';
import { getSafeWhatsappGroupUrl } from '../../utils/whatsappGroupUrl';

// Open-redirect protection. After a successful registration the page may
// send the visitor back to the marketing website via a `returnUrl` query
// param. We ONLY ever redirect to an HTTPS URL on an allow-listed host —
// anything else is ignored and the visitor stays on the portal's own
// success screen.
const ALLOWED_RETURN_HOSTS = [
  'almadina-college.com',
  'www.almadina-college.com',
];

function getSafeReturnUrl(raw) {
  if (!raw) return null;
  let url;
  try {
    url = new URL(raw);
  } catch {
    return null; // not a valid absolute URL
  }
  if (url.protocol !== 'https:') return null; // https only
  const host = url.hostname.toLowerCase();
  const ok = ALLOWED_RETURN_HOSTS.some(
    (h) => host === h || host.endsWith(`.${h}`) // host or subdomain
  );
  return ok ? url.toString() : null;
}

function withStatus(rawUrl, status) {
  try {
    const u = new URL(rawUrl);
    u.searchParams.set('status', status);
    return u.toString();
  } catch {
    return rawUrl;
  }
}

/**
 * Public-facing registration page. Renders any RegistrationForm schema
 * from the slug in the URL. No auth, no chrome. The layout is two-pane
 * on desktop (info + image on the right, form on the left) and stacks
 * to a single column on mobile.
 */
export default function PublicRegistration() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [data, setData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [whatsappGroupUrl, setWhatsappGroupUrl] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const redirectTimer = useRef(null);
  const formGeneration = useRef(0);
  const requestToken = useRef(null);
  const submissionGeneration = useRef(0);
  const requiredOnly = searchParams.get('view') === 'required';
  const visibleFields = (form?.fields || []).filter(f => !requiredOnly || f.required);
  const isTamkeen = form?.slug === 'diploma-legal-intellectual';
  const diplomaQuery = attributionQuery(searchParams.toString());
  const diplomaHref = `/tamkeen${diplomaQuery ? `?${diplomaQuery}` : ''}`;

  useEffect(() => {
    submissionGeneration.current++;
    requestToken.current = null;
    setError('');
    setSubmitting(false);
    setDone(null);
    setWhatsappGroupUrl(null);
    setRedirecting(false);
    clearTimeout(redirectTimer.current);
  }, [requiredOnly]);

  // Validated "return to website" target (or null). Read once from the
  // page's own query string — the website appends ?returnUrl=<encoded>.
  const safeReturnUrl = getSafeReturnUrl(searchParams.get('returnUrl'));

  useEffect(() => {
    const generation = ++formGeneration.current;
    requestToken.current = null;
    setDone(null);
    setWhatsappGroupUrl(null);
    setRedirecting(false);
    setSubmitting(false);
    setError('');
    (async () => {
      setLoading(true);
      try {
        const r = await registrationFormService.publicGet(slug);
        if (formGeneration.current !== generation) return;
        if (r?.success) {
          setForm(r.data);
          const seed = {};
          for (const f of r.data.fields) seed[f.key] = f.type === 'checkbox' ? false : '';
          setData(seed);
        } else {
          setError(r?.message === 'not_found'
            ? 'النموذج غير موجود أو غير مفعّل حالياً.'
            : 'تعذّر تحميل النموذج.');
        }
      } catch {
        if (formGeneration.current === generation) setError('تعذّر الاتصال بالخادم. يرجى المحاولة لاحقاً.');
      } finally {
        if (formGeneration.current === generation) setLoading(false);
      }
    })();
    return () => {
      formGeneration.current++;
      clearTimeout(redirectTimer.current);
    };
  }, [slug]);

  const setVal = (k, v) => setData(d => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || (requiredOnly && !visibleFields.length)) return;
    setError('');

    for (const f of visibleFields) {
      const v = data[f.key];
      if (f.required && (v === undefined || v === null || String(v).trim() === '' || (f.type === 'checkbox' && !v))) {
        setError(`الحقل "${f.label}" مطلوب`);
        return;
      }
    }

    // Optional identity/contact fields may be blank, but provided values
    // must still be valid before they are sent to the server.
    for (const f of visibleFields) {
      const value = String(data[f.key] ?? '').trim();
      if (!value) continue;
      if (f.mapsTo === 'idNumber' || f.key === 'idnumber') {
        const idErr = nationalIdError(value);
        if (idErr) { setError(idErr); return; }
      } else if ((f.mapsTo === 'email' || f.type === 'email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setError('أدخل بريداً إلكترونياً صحيحاً.');
        return;
      } else if ((f.mapsTo === 'phone' || f.type === 'phone') &&
        (!/^\+?[\d\s().-]+$/.test(value) || value.replace(/\D/g, '').length < 6 || value.replace(/\D/g, '').length > 15)) {
        setError('أدخل رقم هاتف صحيحاً.');
        return;
      }
    }
    setSubmitting(true);
    const generation = formGeneration.current;
    const visibilityGeneration = submissionGeneration.current;
    const isCurrent = () => formGeneration.current === generation && submissionGeneration.current === visibilityGeneration;
    try {
      if (!requestToken.current) requestToken.current = submissionToken();
      // Whitelist rendered fields: optional entries may still exist in memory
      // after switching views, but must never cross the API boundary hidden.
      const payload = Object.fromEntries(visibleFields.map(f => [f.key, data[f.key]]));
      const r = await registrationFormService.publicSubmit(slug, payload, attributionQuery(searchParams.toString()), requestToken.current, requiredOnly ? 'initial' : undefined);
      if (!isCurrent()) return;
      if (r?.success) {
        const groupUrl = getSafeWhatsappGroupUrl(r.whatsappGroupUrl ?? r.data?.whatsappGroupUrl);
        setWhatsappGroupUrl(groupUrl);
        setDone(r.message || r.data?.message || form.successMessage || 'تم استلام تسجيلك بنجاح.');
        // Keep Tamkeen's next steps and optional group invitation visible.
        // Other forms without a group retain their return-to-website flow.
        if (safeReturnUrl && !groupUrl && !isTamkeen) {
          setRedirecting(true);
          const target = withStatus(safeReturnUrl, 'success');
          redirectTimer.current = setTimeout(() => window.location.assign(target), 1800);
        }
      } else {
        setError(r?.message || 'فشل الإرسال — حاول لاحقاً.');
      }
    } catch {
      if (isCurrent()) setError('فشل الاتصال — يرجى المحاولة لاحقاً.');
    } finally {
      if (isCurrent()) setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-amber-50 px-4 pt-safe pb-safe">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-700" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-amber-50 px-4 pt-safe pb-safe" dir="rtl">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto" />
            <p className="text-muted-foreground">{error || 'النموذج غير متاح.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasHero = !!form.heroImageUrl;
  const hasSchedule = form.schedule?.startTime || form.schedule?.endTime || form.schedule?.dayLabel;
  const hasDetails = !!(
    form.instructor?.name || hasSchedule ||
    form.location || form.deliveryMode || form.contactPhone || form.contactWhatsapp
  );
  // When there's no image and no info to show, the right-hand column would be
  // empty — so we drop the two-pane grid and center the form on desktop.
  const hasInfoColumn = hasHero || hasDetails;

  return (
    <div
      className="min-h-[100dvh] bg-gradient-to-br from-emerald-50 via-white to-amber-50 pt-safe pb-safe sm:pt-10 sm:pb-10 px-3 [overflow-wrap:anywhere]"
      dir="rtl"
    >
      <div className="max-w-6xl mx-auto">
        {/* Brand header */}
        <div className="text-center mb-6 sm:mb-8">
          <img
            src="/logo-wordmark.png"
            alt="كلية المدينة — Almadina College"
            className="mx-auto h-16 sm:h-20 w-auto"
          />
        </div>

        {done ? (
          <Card className="max-w-xl mx-auto shadow-xl border-emerald-200">
            <CardContent className="p-5 sm:p-10 text-center space-y-4">
              <div className="inline-flex p-3 bg-emerald-100 rounded-full">
                <CheckCircle2 className="h-14 w-14 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-900">تم استلام طلبك</h2>
              <p className="text-muted-foreground whitespace-pre-line leading-relaxed">{done}</p>
              {whatsappGroupUrl && (
                <div className="space-y-3 pt-2">
                  <p className="text-sm text-muted-foreground">يمكنك الآن الانضمام إلى مجموعة المساق على واتساب.</p>
                  <a
                    className={buttonVariants({ className: 'w-full sm:w-auto min-h-12 h-auto py-3 whitespace-normal bg-emerald-700 hover:bg-emerald-800' })}
                    href={whatsappGroupUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="h-5 w-5 ml-2 shrink-0" />
                    الانضمام إلى مجموعة واتساب
                  </a>
                </div>
              )}
              {isTamkeen && (
                <div className="pt-2">
                  <Button
                    asChild
                    className="w-full sm:w-auto min-h-12 h-auto px-6 py-3 rounded-full bg-slate-900 text-white hover:bg-slate-800 shadow-sm whitespace-normal"
                  >
                    <a href={diplomaHref}>
                      <GraduationCap className="h-5 w-5 shrink-0" />
                      تعرّف على دبلوم التمكين
                      <ArrowLeft className="h-4 w-4 shrink-0" />
                    </a>
                  </Button>
                </div>
              )}
              {safeReturnUrl && (
                <div className="pt-2 space-y-3">
                  {redirecting && (
                    <p className="inline-flex items-center gap-2 text-sm text-emerald-700">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      جارٍ إعادتك إلى موقع كلية المدينة...
                    </p>
                  )}
                  <Button
                    onClick={() => window.location.assign(withStatus(safeReturnUrl, 'success'))}
                    className="bg-emerald-700 hover:bg-emerald-800"
                  >
                    العودة إلى موقع كلية المدينة
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className={hasInfoColumn ? 'grid lg:grid-cols-5 gap-5 lg:gap-6' : 'max-w-2xl mx-auto'}>

            {/* ─── INFO COLUMN (right in RTL) ─── */}
            {hasInfoColumn && (
            <div className="lg:col-span-2 space-y-4 order-1 lg:order-2">
              {hasHero && (
                <Card className="overflow-hidden shadow-xl border-emerald-100">
                  <div className="bg-emerald-900/5 relative">
                    <img
                      src={form.heroImageUrl}
                      alt={form.title}
                      className="w-full h-auto"
                      loading="eager"
                    />
                  </div>
                </Card>
              )}

              {hasDetails && (
                <Card className="shadow-lg border-emerald-100">
                  <CardContent className="p-5 space-y-4">
                    {form.instructor?.name && (
                      <InfoRow icon={<Users className="h-5 w-5" />} label={form.instructor.title || 'المحاضر'}>
                        <span className="font-semibold text-slate-800">{form.instructor.name}</span>
                      </InfoRow>
                    )}

                    {form.schedule?.dayLabel && (
                      <InfoRow icon={<Calendar className="h-5 w-5" />} label="الموعد">
                        <span className="font-semibold text-slate-800">{form.schedule.dayLabel}</span>
                      </InfoRow>
                    )}

                    {(form.schedule?.startTime || form.schedule?.endTime) && (
                      <InfoRow icon={<Clock className="h-5 w-5" />} label="التوقيت">
                        <span className="font-semibold text-slate-800" dir="ltr">
                          {form.schedule.startTime}{form.schedule.endTime ? ` – ${form.schedule.endTime}` : ''}
                        </span>
                      </InfoRow>
                    )}

                    {form.location && (
                      <InfoRow icon={<MapPin className="h-5 w-5" />} label="المكان">
                        <span className="font-semibold text-slate-800">{form.location}</span>
                      </InfoRow>
                    )}

                    {form.deliveryMode && (
                      <InfoRow icon={<Video className="h-5 w-5" />} label="نمط التعليم">
                        <span className="font-semibold text-slate-800">{deliveryLabel(form.deliveryMode)}</span>
                      </InfoRow>
                    )}

                    {(form.contactPhone || form.contactWhatsapp) && (
                      <div className="pt-4 border-t border-emerald-100 space-y-2">
                        <p className="text-xs font-semibold text-emerald-800">للاستفسار</p>
                        {form.contactPhone && (
                          <a href={`tel:${form.contactPhone}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-700 transition-colors">
                            <Phone className="h-4 w-4 text-emerald-600" />
                            <span dir="ltr" className="font-mono">{form.contactPhone}</span>
                          </a>
                        )}
                        {form.contactWhatsapp && (
                          <a
                            href={`https://wa.me/${form.contactWhatsapp.replace(/\D/g, '')}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-700 transition-colors"
                          >
                            <MessageCircle className="h-4 w-4 text-emerald-600" />
                            <span dir="ltr" className="font-mono">{form.contactWhatsapp}</span>
                            <span className="text-[10px] text-muted-foreground">واتساب</span>
                          </a>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
            )}

            {/* ─── FORM COLUMN (left in RTL) ─── */}
            <Card className={hasInfoColumn ? 'lg:col-span-3 order-2 lg:order-1 shadow-xl border-emerald-100' : 'shadow-xl border-emerald-100'}>
              <CardContent className="p-4 sm:p-8">
                <div className="mb-6 pb-5 border-b border-emerald-100">
                  <h2 className="text-2xl sm:text-3xl font-bold text-emerald-900 leading-tight">{form.title}</h2>
                  {form.cohortLabel && (
                    <span className="inline-block mt-2 text-sm font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                      {form.cohortLabel}
                    </span>
                  )}
                  {form.description && (
                    <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line leading-relaxed">
                      {form.description}
                    </p>
                  )}
                </div>

                {requiredOnly && !visibleFields.length ? (
                  <p className="rounded-md bg-emerald-50 p-4 text-sm">
                    لا توجد حقول مطلوبة في هذا النموذج. يرجى التواصل مع الكلية لإتمام التسجيل.
                  </p>
                ) : <form onSubmit={submit} className="space-y-4">
                  {visibleFields.map(f => (
                    <FieldRenderer key={f.key} field={f} value={data[f.key]} onChange={(v) => setVal(f.key, v)} />
                  ))}

                  {error && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {form.hasWhatsappGroup && (
                    <p className="text-sm text-emerald-800 bg-emerald-50 rounded-lg p-3 leading-relaxed">
                      بعد التسجيل، سيظهر لك رابط الانضمام إلى مجموعة المساق على واتساب.
                    </p>
                  )}
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 text-base font-semibold bg-emerald-700 hover:bg-emerald-800 shadow-md hover:shadow-lg transition-all"
                  >
                    {submitting && <Loader2 className="h-5 w-5 ml-2 animate-spin" />}
                    {form.submitButtonText || 'إرسال'}
                  </Button>
                </form>}
              </CardContent>
            </Card>
          </div>
        )}

        <p className="text-center text-xs text-emerald-800/60 mt-6 sm:mt-8">
          © {new Date().getFullYear()} كلية المدينة
        </p>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-emerald-700/70 font-semibold">{label}</p>
        <div className="text-sm mt-0.5 break-words">{children}</div>
      </div>
    </div>
  );
}

function deliveryLabel(mode) {
  if (mode === 'in_person') return 'حضوري';
  if (mode === 'zoom')      return 'عن بُعد (Zoom)';
  if (mode === 'hybrid')    return 'حضوري + إمكانية المتابعة عبر Zoom';
  return '';
}

export function FieldRenderer({ field, value, onChange }) {
  const id = `f_${field.key}`;
  const reqMark = field.required ? <span className="text-red-500"> *</span> : null;

  if (field.type === 'textarea') {
    return (
      <div>
        <Label htmlFor={id} className="mb-1.5 block font-medium">{field.label}{reqMark}</Label>
        <Textarea id={id} value={value || ''} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} rows={4} />
        {field.helpText && <p className="text-xs text-muted-foreground mt-1">{field.helpText}</p>}
      </div>
    );
  }
  if (field.type === 'select') {
    return (
      <div>
        <Label htmlFor={id} className="mb-1.5 block font-medium">{field.label}{reqMark}</Label>
        <Select id={id} value={value || ''} onChange={(e) => onChange(e.target.value)}>
          <SelectOption value="">{field.placeholder || 'اختر…'}</SelectOption>
          {(field.options || []).map(opt => <SelectOption key={opt} value={opt}>{opt}</SelectOption>)}
        </Select>
        {field.helpText && <p className="text-xs text-muted-foreground mt-1">{field.helpText}</p>}
      </div>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-50/40 border border-emerald-100">
        <Checkbox id={id} checked={!!value} onCheckedChange={onChange} />
        <Label htmlFor={id} className="leading-snug font-normal">{field.label}{reqMark}</Label>
      </div>
    );
  }
  const inputType =
    field.type === 'email'  ? 'email'  :
    field.type === 'phone'  ? 'tel'    :
    field.type === 'number' ? 'number' :
    field.type === 'date'   ? 'date'   : 'text';
  const isNationalId = field.mapsTo === 'idNumber' || field.key === 'idnumber';
  return (
    <div className="min-w-0 w-full max-w-full">
      <Label htmlFor={id} className="mb-1.5 block font-medium">{field.label}{reqMark}</Label>
      <Input
        id={id}
        type={inputType}
        value={value ?? ''}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-11"
        {...(isNationalId ? { inputMode: 'numeric', maxLength: 9 } : {})}
      />
      {field.helpText && <p className="text-xs text-muted-foreground mt-1">{field.helpText}</p>}
    </div>
  );
}
